import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AgencyUsersService, PendingProAccount } from '../../../services/agency-users.service';
import { FlagService } from '../../../shared/services/flag.service';
import { FlagType } from '../../../shared/utils/enums';
import { ProAccountRejectDialogComponent } from './pro-account-reject-dialog.component';

/** Statuts pro proposés au validateur (alignés sur les 3 profils du PDF client : remplaçant / titulaire / vacataire). */
export const PRO_STATUS_OPTIONS: { value: 'VACATAIRE' | 'TITULAIRE' | 'REMPLACANT'; label: string }[] = [
  { value: 'VACATAIRE', label: 'Vacataire' },
  { value: 'TITULAIRE', label: 'Titulaire' },
  { value: 'REMPLACANT', label: 'Remplaçant' },
];

interface PendingRow extends PendingProAccount {
  /** Statut pro choisi par le validateur (pré-rempli avec celui de l'inscription). */
  selectedProStatus: string;
  busy: boolean;
}

/**
 * « Comptes professionnels » — validation des comptes créés par les professionnels eux-mêmes
 * (PDF client §3). Accessible avec le droit VALIDATE_PRO_ACCOUNT (Admin, Assistant/Secrétaire,
 * Responsable RH par défaut). Un compte en attente peut se connecter mais ne peut pas postuler.
 */
@Component({
  selector: 'app-pro-accounts',
  templateUrl: './pro-accounts.component.html',
  styleUrl: './pro-accounts.component.scss',
})
export class ProAccountsComponent implements OnInit {
  readonly proStatusOptions = PRO_STATUS_OPTIONS;

  loading = false;
  loadError = false;
  rows: PendingRow[] = [];

  constructor(private usersService: AgencyUsersService,
              private dialog: MatDialog,
              private flag: FlagService) {
  }

  ngOnInit(): void {
    this.load();
  }

  /** Règle client : médecin = Remplaçant uniquement ; infirmier(ère) = Vacataire ou Titulaire (liste fournie par le back). */
  statusOptionsFor(a: PendingRow) {
    const allowed = a.allowedProStatuses;
    return allowed?.length ? this.proStatusOptions.filter((o) => allowed.includes(o.value)) : this.proStatusOptions;
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.usersService.listPendingProAccounts().subscribe({
      next: (res) => {
        this.rows = (res?.items ?? []).map((a) => ({ ...a, selectedProStatus: a.proStatus ?? 'VACATAIRE', busy: false }));
        this.usersService.setPendingProCount(this.rows.length);
        this.loading = false;
      },
      error: (err) => {
        this.rows = [];
        this.loading = false;
        this.loadError = true;
        this.flag.displayFlag(AgencyUsersService.errorMessage(err, 'Impossible de charger les comptes en attente.'), FlagType.error);
      },
    });
  }

  fullName(a: PendingProAccount): string {
    return `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.email;
  }

  /** Libellé du profil du PDF : « Médecin — Remplaçant », « Infirmier(ère) — Vacataire »… */
  profileLabel(a: PendingRow): string {
    const status = this.proStatusOptions.find((o) => o.value === a.selectedProStatus)?.label ?? '';
    return [a.fonction, status].filter(Boolean).join(' — ');
  }

  date(value?: string): string {
    if (!value) {
      return '—';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return value;
    }
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  validate(a: PendingRow): void {
    if (a.busy) {
      return;
    }
    a.busy = true;
    // on n'envoie le statut que s'il a été corrigé : sinon le back conserve celui de l'inscription
    const override = a.selectedProStatus !== a.proStatus ? a.selectedProStatus : undefined;
    this.usersService.validateProAccount(a.id, override).subscribe({
      next: () => {
        this.flag.displayFlag(`Compte de ${this.fullName(a)} validé : le professionnel a été prévenu.`, FlagType.success);
        this.load();
      },
      error: (err) => {
        a.busy = false;
        this.flag.displayFlag(AgencyUsersService.errorMessage(err, 'La validation a échoué.'), FlagType.error);
        // un autre validateur l'a peut-être déjà traité : on recharge la liste
        this.load();
      },
    });
  }

  reject(a: PendingRow): void {
    if (a.busy) {
      return;
    }
    this.dialog.open(ProAccountRejectDialogComponent, {
      width: '480px',
      autoFocus: false,
      data: { name: this.fullName(a), email: a.email },
    }).afterClosed().subscribe((result?: { confirm: boolean; reason?: string }) => {
      if (!result?.confirm) {
        return;
      }
      a.busy = true;
      this.usersService.rejectProAccount(a.id, result.reason).subscribe({
        next: () => {
          this.flag.displayFlag(`Demande de ${this.fullName(a)} refusée : le professionnel a été prévenu.`, FlagType.success);
          this.load();
        },
        error: (err) => {
          a.busy = false;
          this.flag.displayFlag(AgencyUsersService.errorMessage(err, 'Le refus a échoué.'), FlagType.error);
          this.load();
        },
      });
    });
  }
}
